<script setup lang="ts">
  import { useJourneyTemplateStore } from '@/store/journey-template.store'
  import type { JourneyTemplate } from '@/types/journey-template'
  import { Modal, require, UiButton, useToastStore } from '@logistiek-zonder-papier/lzp-library'
  import { type Ref, ref } from 'vue'

  const journeyTemplateStore = useJourneyTemplateStore()
  const toastStore = useToastStore()

  const show: Ref<boolean> = ref(false)
  const template: Ref<null | JourneyTemplate> = ref(null)
  const organizationName: Ref<string> = ref('BV DEVA Recycling')
  const isSubmitting: Ref<boolean> = ref(false)

  const startSharing: Ref<boolean> = ref(false)

  function setJourneyTemplate(journeyTemplate: JourneyTemplate) {
    template.value = journeyTemplate
    startSharing.value = journeyTemplate.sharedWith == null

    organizationName.value = journeyTemplate.sharedWith?.organisatieNaam ?? 'BV DEVA Recycling'
  }

  function toggleModal() {
    show.value = !show.value
  }

  async function submit() {
    isSubmitting.value = true

    if (template.value?.sharedWith == null) {
      try {
        await journeyTemplateStore.startSharing(require(template.value))
        toastStore.success('Starten met delen is gelukt!')
      } catch (error) {
        toastStore.error('Er is iets misgegaan met het starten van delen.')
        isSubmitting.value = false
        throw error
      }
    } else {
      try {
        await journeyTemplateStore.stopSharing(require(template.value))
        toastStore.success('Stoppen met delen is gelukt!')
      } catch (error) {
        toastStore.error('Er is iets misgegaan met het stoppen met delen.')
        isSubmitting.value = false
        throw error
      }
    }

    dispatchEvent(new CustomEvent('reload-dataTable'))
    show.value = false
    isSubmitting.value = false
  }

  defineExpose({ toggleModal, setJourneyTemplate })
</script>

<template>
  <Modal v-model:show="show">
    <template #header>
      <h3 v-if="startSharing">Delen met {{ organizationName }}</h3>
      <h3 v-else>Stop delen met {{ organizationName }}</h3>
    </template>
    <div v-if="startSharing">
      <p>
        Door deze stroom te delen kan {{ organizationName }} alle ritten die met deze stroom
        verbonden zijn zien in hun rapportage.
      </p>
    </div>
    <div v-else>
      <p>
        Door deze stroom niet meer te delen kan {{ organizationName }} niet meer de ritten die met
        deze stroom verbonden zijn zien in hun rapportage.
      </p>
    </div>
    <template #footer="{ close }">
      <UiButton variant="default" type="submit" label="Annuleren" @click="close" />
      <UiButton
        variant="prominent"
        type="submit"
        :label="startSharing ? 'Beginnen met delen' : 'Stoppen met delen'"
        :disabled="isSubmitting"
        @click="submit"
      />
    </template>
  </Modal>
</template>

<style lang="css" scoped></style>
